import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { NET_COLLECTIONS, NET_STATUSES, NET_EXTRA_FIELDS } from './collections';

const Field = ({ label, children }) => (
    <div>
        <label className="text-xs font-medium text-foreground/70 block mb-1">{label}</label>
        {children}
    </div>
);

export default function NetworkContactForm({ open, onClose, onSaved, api, contact }) {
    const isEdit = !!contact?.id;
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});

    useEffect(() => {
        if (open) {
            setForm(contact ? { ...contact, extra: { ...(contact.extra || {}) } } : { collection: 'decideurs', status_relation: 'prospect', extra: {} });
        }
    }, [open, contact]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setExtra = (k, v) => setForm(f => ({ ...f, extra: { ...(f.extra || {}), [k]: v } }));

    const coll = form.collection || 'decideurs';
    const meta = NET_COLLECTIONS[coll];
    const extraFields = NET_EXTRA_FIELDS[coll] || [];

    const save = async () => {
        if (!form.full_name && !form.first_name && !form.last_name) {
            toast.error('Renseignez au moins un nom'); return;
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                tags: typeof form.tags === 'string'
                    ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
                    : (form.tags || []),
                score_total: form.score_total === '' || form.score_total == null ? null : Number(form.score_total),
            };
            if (isEdit) await api.put(`/network/contacts/${contact.id}`, body);
            else await api.post('/network/contacts', body);
            toast.success(isEdit ? 'Contact mis à jour' : 'Contact ajouté au réseau');
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Échec de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="network-contact-form">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        {isEdit ? 'Modifier le contact' : 'Nouveau contact réseau'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5">
                    <Field label="Collection *">
                        <select
                            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                            value={coll} disabled={isEdit}
                            onChange={e => set('collection', e.target.value)}
                            data-testid="network-form-collection"
                        >
                            {Object.entries(NET_COLLECTIONS).map(([k, m]) => (
                                <option key={k} value={k}>{m.label}</option>
                            ))}
                        </select>
                    </Field>

                    {meta.isPerson ? (
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Civilité">
                                <Input value={form.civility || ''} onChange={e => set('civility', e.target.value)} placeholder="M. / Mme / Dr / S.E." />
                            </Field>
                            <Field label="Prénom">
                                <Input value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} data-testid="network-form-firstname" />
                            </Field>
                            <Field label="Nom *">
                                <Input value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} data-testid="network-form-lastname" />
                            </Field>
                        </div>
                    ) : (
                        <Field label={coll === 'organisations' ? 'Nom de l\'organisation *' : 'Nom complet *'}>
                            <Input value={form.full_name || ''} onChange={e => set('full_name', e.target.value)} data-testid="network-form-fullname" />
                        </Field>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Fonction">
                            <Input value={form.role_function || ''} onChange={e => set('role_function', e.target.value)} />
                        </Field>
                        <Field label="Organisation">
                            <Input value={form.organization || ''} onChange={e => set('organization', e.target.value)} />
                        </Field>
                        <Field label="Pays">
                            <Input value={form.country || ''} onChange={e => set('country', e.target.value)} data-testid="network-form-country" />
                        </Field>
                        <Field label="Ville">
                            <Input value={form.city || ''} onChange={e => set('city', e.target.value)} />
                        </Field>
                        <Field label="Continent">
                            <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                    value={form.continent || ''} onChange={e => set('continent', e.target.value)}>
                                <option value="">—</option>
                                {['Afrique', 'Europe', 'Amérique du Nord', 'Amérique du Sud', 'Asie', 'Océanie'].map(c => <option key={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Email">
                            <Input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} data-testid="network-form-email" />
                        </Field>
                        <Field label="Email secondaire">
                            <Input type="email" value={form.email_secondary || ''} onChange={e => set('email_secondary', e.target.value)} />
                        </Field>
                        <Field label="Téléphone">
                            <Input value={form.phone || ''} onChange={e => set('phone', e.target.value)} />
                        </Field>
                        <Field label="LinkedIn">
                            <Input value={form.linkedin || ''} onChange={e => set('linkedin', e.target.value)} />
                        </Field>
                        <Field label="Site web">
                            <Input value={form.website || ''} onChange={e => set('website', e.target.value)} />
                        </Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Statut relationnel">
                            <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                    value={form.status_relation || 'prospect'} onChange={e => set('status_relation', e.target.value)}
                                    data-testid="network-form-status">
                                {Object.entries(NET_STATUSES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Priorité">
                            <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                    value={form.priority || ''} onChange={e => set('priority', e.target.value)}>
                                <option value="">—</option>
                                {['P1', 'P2', 'P3', 'Haute', 'Moyenne', 'Basse'].map(p => <option key={p}>{p}</option>)}
                            </select>
                        </Field>
                        <Field label="Score total">
                            <Input type="number" value={form.score_total ?? ''} onChange={e => set('score_total', e.target.value)} />
                        </Field>
                    </div>

                    {extraFields.length > 0 && (
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-2">
                                Champs spécifiques — {meta.label}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {extraFields.map(([k, label]) => (
                                    <Field key={k} label={label}>
                                        <Input value={form.extra?.[k] || ''} onChange={e => setExtra(k, e.target.value)}
                                               data-testid={`network-form-extra-${k}`} />
                                    </Field>
                                ))}
                            </div>
                        </div>
                    )}

                    <Field label="Tags (séparés par des virgules)">
                        <Input
                            value={Array.isArray(form.tags) ? form.tags.join(', ') : (form.tags || '')}
                            onChange={e => set('tags', e.target.value)}
                            placeholder="vip, sommet-2026, energie"
                            data-testid="network-form-tags"
                        />
                    </Field>

                    <Field label="Notes">
                        <Textarea rows={3} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
                    </Field>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={save} disabled={saving} data-testid="network-form-save">
                        {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
                        {isEdit ? 'Enregistrer' : 'Ajouter au réseau'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
